import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  Search, 
  MoreHorizontal, 
  Settings, 
  Users, 
  Menu, 
  X,
  Plus,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  Minus
} from 'lucide-react';

const QuestionGroupsManagement = () => {
  const [currentView, setCurrentView] = useState('groups'); // 'groups' or 'group-detail'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    groupName: '',
    type: '',
    question: '',
    options: [
      { id: 1, text: '' },
      { id: 2, text: '' }
    ]
  });

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mock data for question groups
  const questionGroups = [
    { id: 1, name: 'Basics', count: 12 },
    { id: 2, name: 'Background & Identity', count: 12 },
    { id: 3, name: 'Lifestyle', count: 12 },
    { id: 4, name: 'Family', count: 12 },
    { id: 5, name: 'Interests', count: 20 }
  ];

  // Mock data for questions within a group
  const groupQuestions = [
    { id: 1, text: 'Question', type: 'Single Choice' },
    { id: 2, text: 'How tall are you?', type: 'Single Choice' },
    { id: 3, text: 'How tall are you?', type: 'Text field' },
    { id: 4, text: 'How tall are you?', type: 'Description' },
    { id: 5, text: 'How tall are you?', type: 'Multiple Choice' },
    { id: 6, text: 'How tall are you?', type: 'Single Choice' }
  ];

  const questionTypes = ['Free Text', 'Single Choice', 'Multiple Choice', 'Slider'];

  const filteredItems = currentView === 'groups' 
    ? questionGroups.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : groupQuestions.filter(question => 
        question.text.toLowerCase().includes(searchTerm.toLowerCase())
      );

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(filteredItems.map(item => item.id));
      setSelectedItems(allIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId, checked) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    setSelectedItems(newSelection);
  };

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setCurrentView('group-detail');
    setSelectedItems(new Set());
    setSearchTerm('');
  };

  const handleBackToGroups = () => {
    setCurrentView('groups');
    setSelectedGroup(null);
    setSelectedItems(new Set());
    setSearchTerm('');
  };

  const handleAddGroup = () => {
    setFormData({
      groupName: '',
      type: '',
      question: '',
      options: [
        { id: 1, text: '' },
        { id: 2, text: '' }
      ]
    });
    setShowAddModal(true);
  };

  const handleAddMoreOption = () => {
    const newId = Math.max(...formData.options.map(o => o.id)) + 1;
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { id: newId, text: '' }]
    }));
  };

  const handleRemoveOption = (optionId) => {
    if (formData.options.length > 2) {
      setFormData(prev => ({
        ...prev,
        options: prev.options.filter(o => o.id !== optionId)
      }));
    }
  };

  const handleOptionChange = (optionId, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => 
        o.id === optionId ? { ...o, text: value } : o
      )
    }));
  };

  const handleFormSubmit = () => {
    console.log('Form submitted:', formData);
    setShowAddModal(false);
  };

  const toggleDropdown = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  const handleDropdownAction = (action, item) => {
    setOpenDropdown(null);
    console.log(`${action} action for:`, item);
  };

  const shouldShowOptions = () => {
    return formData.type === 'Single Choice' || formData.type === 'Multiple Choice';
  };

  const renderBreadcrumb = () => {
    const breadcrumbItems = [
      { label: 'Dashboard', path: 'dashboard' },
      { label: 'Profile Metadata', path: 'profile-metadata' },
      { label: 'Question Groups', path: 'question-groups' }
    ];

    if (currentView === 'group-detail' && selectedGroup) {
      breadcrumbItems.push({ label: selectedGroup.name, path: 'group-detail' });
    }

    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        {breadcrumbItems.map((item, index) => (
          <React.Fragment key={item.path}>
            <span className={index === breadcrumbItems.length - 1 ? 'text-gray-900' : 'text-gray-500'}>
              {item.label}
            </span>
            {index < breadcrumbItems.length - 1 && <ChevronRight className="w-4 h-4" />}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderTableHeader = () => {
    if (currentView === 'groups') {
      return (
        <tr>
          <th className="w-12 px-6 py-3 text-left">
            <input
              type="checkbox"
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded border-gray-300"
            />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Groups
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Count
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      );
    } else {
      return (
        <tr>
          <th className="w-12 px-6 py-3 text-left">
            <Minus className="w-4 h-4 text-gray-400" />
          </th>
          <th className="w-12 px-6 py-3 text-left">
            <input
              type="checkbox"
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded border-gray-300"
            />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Question
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Type
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      );
    }
  };

  const renderTableRow = (item) => {
    if (currentView === 'groups') {
      return (
        <tr key={item.id} className="hover:bg-gray-50">
          <td className="px-6 py-4">
            <input
              type="checkbox"
              checked={selectedItems.has(item.id)}
              onChange={(e) => handleSelectItem(item.id, e.target.checked)}
              className="rounded border-gray-300"
            />
          </td>
          <td 
            className="px-6 py-4 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
            onClick={() => handleGroupClick(item)}
          >
            {item.name}
          </td>
          <td className="px-6 py-4 text-sm text-gray-900">{item.count}</td>
          <td className="px-6 py-4 text-sm text-gray-400">
            <div className="relative" ref={openDropdown === item.id ? dropdownRef : null}>
              <button
                onClick={() => toggleDropdown(item.id)}
                className="hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              
              {openDropdown === item.id && (
                <div className="absolute right-0 top-8 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownAction('edit', item)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDropdownAction('delete', item)}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    } else {
      return (
        <tr key={item.id} className="hover:bg-gray-50">
          <td className="px-6 py-4">
            <Minus className="w-4 h-4 text-gray-400" />
          </td>
          <td className="px-6 py-4">
            <input
              type="checkbox"
              checked={selectedItems.has(item.id)}
              onChange={(e) => handleSelectItem(item.id, e.target.checked)}
              className="rounded border-gray-300"
            />
          </td>
          <td className="px-6 py-4 text-sm text-gray-900">{item.text}</td>
          <td className="px-6 py-4 text-sm text-gray-900">{item.type}</td>
          <td className="px-6 py-4 text-sm text-gray-400">
            <div className="relative" ref={openDropdown === item.id ? dropdownRef : null}>
              <button
                onClick={() => toggleDropdown(item.id)}
                className="hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              
              {openDropdown === item.id && (
                <div className="absolute right-0 top-8 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownAction('edit', item)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDropdownAction('delete', item)}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          {renderBreadcrumb()}
        </div>

        {/* Page Content */}
        <div className="flex-1 p-8">
          <div className="max-w-7xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentView === 'groups' ? 'Question Groups' : selectedGroup?.name}
                </h1>
                <p className="text-gray-600">
                  {currentView === 'groups' 
                    ? 'Add frequently asked questions for users to see on your app and the website.'
                    : 'All the questions in the group are listed here'
                  }
                </p>
              </div>
              <button
                onClick={handleAddGroup}
                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium"
              >
                Add
              </button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search question"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  {renderTableHeader()}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map(renderTableRow)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {selectedItems.size} of {filteredItems.length} row(s) selected.
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Rows per page</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Page 1 of 10</span>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Question Group Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Add Question Group</h2>
                  <p className="text-sm text-gray-500 mt-1">Create a group and add questions within</p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Group Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group name*
                  </label>
                  <input
                    type="text"
                    value={formData.groupName}
                    onChange={(e) => setFormData(prev => ({ ...prev, groupName: e.target.value }))}
                    placeholder="Basics"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type*
                  </label>
                  <div className="relative">
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    >
                      <option value="">Multiple Choice</option>
                      {questionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Question */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question*
                  </label>
                  <textarea
                    value={formData.question}
                    onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                    placeholder="What are you looking for?"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Options - Only show for Single Choice and Multiple Choice */}
                {shouldShowOptions() && (
                  <>
                    {formData.options.map((option, index) => (
                      <div key={option.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Option {index + 1}*
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={option.text}
                            onChange={(e) => handleOptionChange(option.id, e.target.value)}
                            placeholder={index === 0 ? "Casual Dating" : "Serious Relationship"}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <div className="flex items-center">
                            <button
                              onClick={() => handleRemoveOption(option.id)}
                              disabled={formData.options.length <= 2}
                              className={`p-2 rounded border ${
                                formData.options.length <= 2 
                                  ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                                  : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add More Button */}
                    <button
                      onClick={handleAddMoreOption}
                      className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add More</span>
                    </button>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFormSubmit}
                  className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionGroupsManagement;